import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';
import { GridRowParams } from '@material-ui/data-grid';
import { yupResolver } from '@hookform/resolvers/yup';
import { schema, schemaUF, schemaModalCST } from '../validations';
import { useForm } from 'react-hook-form';
import {
  ClassificacaoIbsCbsProviderProps,
  ClassificacaoIbsCbsContextData,
  formType,
} from '../types/context';
import { loadingContext } from '~/context/loading';
import { IbsCbsGetInfos } from '../services/getInfos';
import { IbsCbsServices } from '../services';
import {
  cstProps,
  dataProps,
  formDataProps,
  rowsUfProps,
  selectProps,
  ufProps,
} from '../types';
import { useQuery } from 'react-query';
import { insertOrUpdate, MasterDetailProps } from '~/utils/masterDetail';
import { nanoid } from 'nanoid';
import { formDefault, modalCSTDefault, ufDefault } from '../defaultData';
import { toast } from 'react-toastify';
import { formatCurrencyAsText, transformAsCurrency } from '~/utils/functions';
import Swal from 'sweetalert2';
import withReactContent from 'sweetalert2-react-content';

export const ClassificacaoIbsCbsContext = createContext(
  {} as ClassificacaoIbsCbsContextData,
);

const MySwal = withReactContent(Swal);
export const ClassificacaoIbsCbsProvider: React.FC<
  ClassificacaoIbsCbsProviderProps
> = ({ children }) => {
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const masterDetailDefault = [
    {
      obj_name: 'ufs',
      pk_fields: ['cod_ibs_cbs'],
      itens: {
        insert: [],
        update: [],
        delete: [],
      },
    },
  ];
  const { setLoading, loading } = useContext(loadingContext);
  const [isUpdate, setUpdate] = useState<boolean>(false);
  const [updateItem, setUpdateItem] = useState<boolean>(false);
  const [newCST, setNewCST] = useState<boolean>(true);
  const [showSearch, setShowSearch] = useState<boolean>(true);
  const [flgPadrao, setFlgPadrao] = useState<boolean>(false);
  const [idFlgPadrao, setIdFlgPadrao] = useState<number>(0);
  const [flgInativo, setFlgInativo] = useState<boolean>(false);
  const [rowsUf, setRowsUf] = useState<rowsUfProps[]>([]);
  // selects
  const [cstOptions, setCstOptions] = useState<selectProps[]>([]);
  const [ufOptions, setUfOptions] = useState<selectProps[]>([]);
  // modal CST
  const [openModal, setOpenModal] = useState<boolean>(false);
  const { getCST, getUFs, getUFOnRowClick, getFlgPadrao } = IbsCbsGetInfos;
  const { inativaIbsCbs, ativarIbsCbs, createIbsCbs, updateIbsCbs } =
    IbsCbsServices;

  const [masterDetail, setMasterDetail] =
    useState<MasterDetailProps[]>(masterDetailDefault);
  // form Geral
  const {
    register,
    reset,
    setValue,
    handleSubmit,
    watch,
    clearErrors,
    getValues,
    control,
    setFocus,
    setError,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(schema),
    reValidateMode: 'onBlur',
  });

  const formClassificao: formType = {
    register,
    reset,
    setValue,
    handleSubmit,
    watch,
    clearErrors,
    getValues,
    control,
    setError,
    setFocus,
    errors,
  };
  // form UF's
  const {
    register: registerUF,
    reset: resetUF,
    setValue: setValueUF,
    handleSubmit: handleSubmitUF,
    watch: watchUF,
    clearErrors: clearErrorsUF,
    getValues: getValuesUF,
    control: controlUF,
    setFocus: setFocusUF,
    setError: setErrorUF,
    formState: { errors: errorsUF },
  } = useForm({
    resolver: yupResolver(schemaUF),
    reValidateMode: 'onBlur',
  });

  const formUF: formType = {
    register: registerUF,
    reset: resetUF,
    setValue: setValueUF,
    handleSubmit: handleSubmitUF,
    watch: watchUF,
    clearErrors: clearErrorsUF,
    getValues: getValuesUF,
    control: controlUF,
    setFocus: setFocusUF,
    setError: setErrorUF,
    errors: errorsUF,
  };

  // form Modal CST
  const {
    register: registerModalCST,
    reset: resetModalCST,
    setValue: setValueModalCST,
    handleSubmit: handleSubmitModalCST,
    watch: watchModalCST,
    clearErrors: clearErrorsModalCST,
    getValues: getValuesModalCST,
    control: controlModalCST,
    setFocus: setFocusModalCST,
    setError: setErrorModalCST,
    formState: { errors: errorsModalCST },
  } = useForm({
    resolver: yupResolver(schemaModalCST),
    reValidateMode: 'onBlur',
  });

  const formModalCST: formType = {
    register: registerModalCST,
    reset: resetModalCST,
    setValue: setValueModalCST,
    handleSubmit: handleSubmitModalCST,
    watch: watchModalCST,
    clearErrors: clearErrorsModalCST,
    getValues: getValuesModalCST,
    control: controlModalCST,
    setFocus: setFocusModalCST,
    setError: setErrorModalCST,
    errors: errorsModalCST,
  };

  const { data: dataCST } = useQuery({
    queryKey: ['get-cst'],
    queryFn: getCST,
    enabled: newCST,
  });

  useEffect(() => {
    if (!dataCST) return;
    const options = dataCST.data.map((item: cstProps) => {
      return {
        value: item.cod_cst_ibs_cbs,
        label: `${item.cst_ibs_cbs} - ${item.des_cst_ibs_cbs}`,
      };
    });
    setCstOptions(options);
    setNewCST(false);
  }, [dataCST]);

  useEffect(() => {
    (async () => {
      const ufs = await getUFs();
      if (ufs.success) {
        const options = ufs.data.map((item: ufProps) => ({
          value: item.cod_uf_ibge,
          label: item.des_sigla,
        }));
        options.sort((a, b) => a.label.localeCompare(b.label));
        options.unshift({
          value: 0,
          label: 'TODAS',
        });

        setUfOptions(options);
      }
    })();
  }, [getUFs]);

  useEffect(() => {
    const error = errors.des_classif_trib;
    if (error) {
      toast.warn(error.message, { toastId: 'des_classif_trib' });
    }
  }, [errors.des_classif_trib]);

  const handleOpenModal = useCallback(
    (open: boolean) => {
      resetModalCST(modalCSTDefault);
      setOpenModal(open);
      if (open) {
        setTimeout(() => {
          setFocusModalCST('des_cst_ibs_cbs');
        }, 50);
      }
    },
    [resetModalCST, setFocusModalCST],
  );

  const handleFlgPadrao = useCallback(
    async (bool: boolean) => {
      if (isUpdate && idFlgPadrao === watch('cod_ibs_cbs') && flgPadrao) {
        const { isConfirmed } = await MySwal.fire({
          text: 'Deseja realmente desmarcar o flag Padrão? É recomendável que outra Classificação seja selecionada como padrão.',
          showCancelButton: true,
          showCloseButton: false,
          showConfirmButton: true,
          confirmButtonColor: '#0065FF',
          cancelButtonColor: '#DE350B',
          showDenyButton: false,
          confirmButtonText: 'SIM',
          cancelButtonText: 'NÃO',
          customClass: { popup: 'swal-wide' },
        });
        if (!isConfirmed) {
          return;
        }
      }
      setFlgPadrao(bool);
    },
    [idFlgPadrao, isUpdate, watch, flgPadrao],
  );

  const handleAddUF = async (cod_ibs_cbs: number) => {
    const result = await getUFOnRowClick(cod_ibs_cbs);
    if (result.success) {
      result.data.map(async (i) => {
        const ufSelecionada = ufOptions.find((uf) => uf.label === i.des_sigla);
        if (!ufSelecionada) return;

        const uuid = nanoid();
        const ufs = {
          uuid,
          des_sigla: ufSelecionada,
          per_aliq_ibs_est: transformAsCurrency(i.per_aliq_ibs_est),
          per_aliq_ibs_mun: transformAsCurrency(i.per_aliq_ibs_mun),
          per_red_ibs: transformAsCurrency(i.per_red_ibs),
        };

        const rowsDetail: any = await insertOrUpdate(
          'ufs',
          ufs,
          masterDetail,
          setMasterDetail,
        );
        setRowsUf(rowsDetail);
      });
    }
  };

  const onRowClick = async (param: GridRowParams) => {
    const { row } = param;
    const {
      cod_ibs_cbs,
      des_cst_ibs_cbs,
      cst_ibs_cbs,
      des_classif_trib,
      cod_cst_ibs_cbs,
      des_ibs_cbs,
      per_aliq_cbs,
      per_red_cbs,
      val_red_social,
      flg_ibs_cbs_padrao,
      flg_inativo,
    } = row;

    reset({
      cod_ibs_cbs,
      des_classif_trib,
      cod_cst_ibs_cbs: {
        value: cod_cst_ibs_cbs,
        label: `${cst_ibs_cbs} - ${des_cst_ibs_cbs}`,
      },
      des_ibs_cbs,
      per_aliq_cbs: formatCurrencyAsText(per_aliq_cbs),
      per_red_cbs: formatCurrencyAsText(per_red_cbs),
      val_red_social: formatCurrencyAsText(val_red_social),
      flg_ibs_cbs_padrao,
    });

    setFlgInativo(flg_inativo);
    setFlgPadrao(flg_ibs_cbs_padrao);

    handleAddUF(cod_ibs_cbs);
    const existFlgPadrao = await getFlgPadrao();
    if (existFlgPadrao.data.length > 0) {
      setIdFlgPadrao(existFlgPadrao.data[0].cod_ibs_cbs);
    }

    setUpdate(true);
    setShowSearch(false);
  };

  const prepFormData = (data: dataProps) => {
    const formData: formDataProps = {
      data: {
        cod_cst_ibs_cbs: data.cod_cst_ibs_cbs,
        des_classif_trib: data.des_classif_trib,
        des_ibs_cbs: data.des_ibs_cbs.trim(),
        per_aliq_cbs: transformAsCurrency(data.per_aliq_cbs),
        per_red_cbs: transformAsCurrency(data.per_red_cbs),
        val_red_social: transformAsCurrency(data.val_red_social),
        flg_inativo: flgInativo,
        flg_padrao: flgPadrao,
      } as dataProps,
      ufs: masterDetail,
    };
    return formData;
  };

  const onSave = handleSubmit(async (data: any) => {
    if (rowsUf.length <= 0) {
      return toast.warn('Ao menos uma UF deve ser informada.');
    }
    setLoading(true);
    const formData: formDataProps = prepFormData(data);

    try {
      if (isUpdate) {
        const { success, message } = await updateIbsCbs(
          watch('cod_ibs_cbs'),
          formData,
        );
        if (success) {
          setShowSearch(true);
          return toast.success(message);
        }
      }
      const { success, message } = await createIbsCbs(formData);
      if (success) {
        setShowSearch(false);
        toast.success(message);
      }
    } catch (error: any) {
      return toast.warn(error.message);
    } finally {
      resetFormData();
      setUpdate(false);
      setLoading(false);
    }
  });

  const resetFormData = useCallback(() => {
    setMasterDetail(JSON.parse(JSON.stringify(masterDetailDefault)));
    reset(formDefault);
    resetModalCST(modalCSTDefault);
    resetUF(ufDefault);
    setRowsUf([]);
    setUpdateItem(false);
    setFlgInativo(false);
    setFlgPadrao(false);
    setIdFlgPadrao(0);
  }, [reset, resetModalCST, resetUF, masterDetailDefault]);

  const handleInativar = async () => {
    try {
      const result = await inativaIbsCbs(watch('cod_ibs_cbs'));
      if (result.success) {
        resetFormData();
        setUpdate(false);
        setShowSearch(true);
        return toast.success(result.message);
      }
    } catch (error: any) {
      return toast.warn(error.message);
    }
  };

  const handleAtivar = async () => {
    try {
      const result = await ativarIbsCbs(watch('cod_ibs_cbs'));
      if (result.success) {
        resetFormData();
        setUpdate(false);
        setShowSearch(true);
        return toast.success(result.message);
      }
    } catch (error: any) {
      return toast.warn(error.message);
    }
  };

  const onNew = useCallback(() => {
    resetFormData();
    setShowSearch(false);
    setUpdate(false);
  }, [resetFormData]);

  return (
    <ClassificacaoIbsCbsContext.Provider
      value={{
        formClassificao,
        formModalCST,
        formUF,
        // estados
        isUpdate,
        setUpdate,
        showSearch,
        setShowSearch,
        loading,
        setLoading,
        openModal,
        cstOptions,
        ufOptions,
        setNewCST,
        rowsUf,
        setRowsUf,
        updateItem,
        setUpdateItem,
        flgPadrao,
        flgInativo,
        setFlgInativo,
        // funções
        onNew,
        resetFormData,
        onRowClick,
        onSave,
        handleInativar,
        handleAtivar,
        handleOpenModal,
        handleFlgPadrao,

        masterDetail,
        setMasterDetail,
      }}
    >
      {children}
    </ClassificacaoIbsCbsContext.Provider>
  );
};
